import { inject, type InjectionKey } from 'vue';

export {
	HUB_APP_ACCESS,
	canSwitchHubApps,
	getAccessibleHubAppIds,
	type HubAppId,
} from './hubApps';

/** Host-provided capabilities passed to micro-frontends mounted in the hub shell. */
export interface ShellContext {
	/** Current Keycloak access token for API calls and role checks. */
	accessToken?: string;
	/** Native app version string from the Capacitor host (e.g. `1.0.45`). */
	appVersion?: string;
	/** Navigate within the hub router (e.g. `/launcher`, `/apps/machines`). */
	navigate: (path: string) => void;
	/** DOM container element where the sub-app should render. */
	getContainer: () => HTMLElement;
	/**
	 * Clears hub local auth and routes to reauth when a sub-app receives HTTP 401.
	 * Must not end the Keycloak SSO session — preserve the SSO cookie for silent re-auth.
	 * Must keep the last-identity snapshot and forget the last hub app.
	 */
	handleUnauthorized?: () => void | Promise<void>;
	/**
	 * Intentional user sign-out from a sub-app (sidebar, top bar, profile).
	 * Must revoke tokens and end the Keycloak SSO session when SSO was used.
	 */
	handleSignOut?: () => void | Promise<void>;
	/** Open the hub settings drawer (DualPane gear). Host-owned; not a route. */
	openSettings?: () => void;
}

/** Handle returned when a sub-app is mounted; call `unmount` to tear it down. */
export interface AppHandle {
	unmount: () => void | Promise<void>;
	/**
	 * Push a refreshed hub access token into the mounted sub-app without remounting.
	 * Required so API clients keep using a live bearer after Keycloak refresh.
	 */
	updateAccessToken?: (token: string) => void | Promise<void>;
	/**
	 * Sync the embedded app router to a hub sub-path (e.g. `share`, `c/123`) without remounting.
	 * Needed when the hub URL changes while the same app stays mounted (warm-start deep links).
	 */
	syncPath?: (subPath: string) => void | Promise<void>;
}

/** Contract implemented by each hub micro-frontend module. */
export interface AppModule {
	id: string;
	name: string;
	description?: string;
	icon?: string;
	/** Mount the app into `el` using the shared shell context. */
	mount: (el: HTMLElement, ctx: ShellContext) => Promise<AppHandle>;
}

/** Launcher registry entry describing a lazy-loaded hub app. */
export interface AppRegistryEntry {
	id: string;
	name: string;
	description?: string;
	icon?: string;
	/** Keycloak realm role required to see and launch this app (e.g. `app:machines`). */
	requiredRole?: string;
	/** When true, the tile is shown on the launcher but cannot be opened yet. */
	disabled?: boolean;
	/** Dynamic import that resolves to an {@link AppModule}. */
	load: () => Promise<AppModule>;
}

/**
 * Vue injection key for the {@link ShellContext}. Mounted sub-apps `provide` this
 * so in-app components can resolve host capabilities (navigation, access token).
 */
export const SHELL_CONTEXT_KEY: InjectionKey<ShellContext> = Symbol('apexx.shellContext');

/**
 * Inject the host {@link ShellContext} inside a mounted sub-app. Returns `undefined`
 * when the app runs standalone (no host provided the context).
 */
export function useShellContext(): ShellContext | undefined {
	return inject(SHELL_CONTEXT_KEY, undefined);
}

export {
	handleUnauthorizedSession,
	isAuthenticatedAxiosUnauthorized,
	resolveUnauthorizedRedirectPath,
	type UnauthorizedRedirectPath,
	type UnauthorizedSessionOptions,
} from './unauthorizedSession';
export {
	handleSignOutSession,
	type SignOutSessionOptions,
} from './signOutSession';
export { accessTokenLogFingerprint } from './accessTokenLog';
export {
	clearSoftUnauthorizedCircuit,
	recordSoftUnauthorizedAttempt,
	SOFT_UNAUTHORIZED_CIRCUIT_KEY,
	SOFT_UNAUTHORIZED_ESCALATE_AFTER,
	SOFT_UNAUTHORIZED_WINDOW_MS,
	type SoftUnauthorizedCircuitDecision,
	type SoftUnauthorizedCircuitOptions,
	type SoftUnauthorizedCircuitState,
} from './softUnauthorizedCircuit';
export {
	bootstrapSubappKeycloakSession,
	createKeycloakSessionExpiredHandler,
	type BootstrapSubappKeycloakSessionOptions,
	type KeycloakSessionExpiredHandlerOptions,
} from './keycloakSessionExpired';
export { useUnauthorizedHandler, type UseUnauthorizedHandlerOptions } from './useUnauthorizedHandler';
export {
	resolveAppUpdateDisplayVersions,
	toAppDisplayVersion,
	type AppUpdateDisplayInput,
	type AppUpdateDisplayResult,
} from './appVersion';
export {
	resolvePushHubDestination,
	type ChannelMessagePushData,
	type FileChangedPushData,
	type PushDataBase,
	type PushDataRecord,
	type PushHubDestination,
} from './pushRouting';
